import { jsPDF } from "jspdf";
import logoImg from "../../../frontend/src/assets/images/filenders_logo.png";
const safeText = (value) => {
    if (value === null || value === undefined) return "";
    return String(value)
        .replace(/Ğ/g, "G")
        .replace(/Ü/g, "U")
        .replace(/Ş/g, "S")
        .replace(/İ/g, "I")
        .replace(/Ö/g, "O")
        .replace(/Ç/g, "C")
        .replace(/ğ/g, "g")
        .replace(/ü/g, "u")
        .replace(/ş/g, "s")
        .replace(/ı/g, "i")
        .replace(/ö/g, "o")
        .replace(/ç/g, "c");
};

const formatDate = (dateValue) => {
    if (!dateValue) return "";
    try {
        const d = new Date(dateValue);
        if (Number.isNaN(d.getTime())) return "";
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    } catch {
        return "";
    }
};

const formatNumber = (value, digits = 2) => {
    const num = Number(value || 0);
    if (Number.isNaN(num)) return "";
    return safeText(
        num.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: digits,
        })
    );
};

const addressLines = (location) => {
    if (!location) return [];

    const additionalInfoLines = Array.isArray(location.additionalInfo)
        ? location.additionalInfo.filter(Boolean).map(safeText)
        : [safeText(location.additionalInfo)].filter(Boolean);

    const line2 = [safeText(location.zipcode), safeText(location.district), safeText(location.city)]
        .filter(Boolean)
        .join(" ");
    const line3 = safeText(location.country);

    return [...additionalInfoLines, line2, line3].filter(Boolean);
};

const box = (doc, x, y, w, h) => {
    doc.rect(x, y, w, h);
};

const line = (doc, x1, y1, x2, y2) => {
    doc.line(x1, y1, x2, y2);
};

const boxTitle = (doc, x, y, no, en, tr) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    if (no !== "") doc.text(String(no), x + 1.4, y + 3.6);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(5.2);
    if (en) doc.text(safeText(en), x + 6.2, y + 3.5);
    if (tr) doc.text(safeText(tr), x + 6.2, y + 6.5);
};

const drawCellHeader = (
    doc,
    x,
    y,
    w,
    no,
    en,
    tr,
    {
        noFontSize = 5.2,
        textFontSize = 4.2,
        noX = 1.2,
        textX = 6.2,
        enY = 3.0,
        trY = 5.4,
        maxEnLines = 2,
        maxTrLines = 2,
        availableWidth = null,
    } = {}
) => {
    const textWidth = availableWidth ?? w - 7.5;

    if (no !== "") {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(noFontSize);
        doc.text(String(no), x + noX, y + enY);
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(textFontSize);

    if (en) {
        const enLines = doc.splitTextToSize(safeText(en), textWidth).slice(0, maxEnLines);
        enLines.forEach((txt, i) => {
            doc.text(txt, x + textX, y + enY + i * 1.9);
        });
    }

    if (tr) {
        const trLines = doc.splitTextToSize(safeText(tr), textWidth).slice(0, maxTrLines);
        trLines.forEach((txt, i) => {
            doc.text(txt, x + textX, y + trY + i * 1.9);
        });
    }
};

const drawTinyHeader = (doc, x, y, w, no, en, tr) => {
    drawCellHeader(doc, x, y, w, no, en, tr, {
        noFontSize: 4.8,
        textFontSize: 3.6,
        noX: 1.0,
        textX: 5.3,
        enY: 2.5,
        trY: 4.5,
        maxEnLines: 2,
        maxTrLines: 2,
        availableWidth: w - 6.3,
    });
};

const drawMediumHeader = (doc, x, y, w, no, en, tr) => {
    drawCellHeader(doc, x, y, w, no, en, tr, {
        noFontSize: 5.0,
        textFontSize: 4.0,
        noX: 1.1,
        textX: 5.8,
        enY: 2.5,
        trY: 4.8,
        maxEnLines: 2,
        maxTrLines: 2,
        availableWidth: w - 7,
    });
};

const drawMergedHeaderSegment = (doc, x, y, w, no, en, tr) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(4.8);
    doc.text(String(no), x + 0.9, y + 2.6);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(3.5);

    const enLines = doc.splitTextToSize(safeText(en), w - 2).slice(0, 2);
    const trLines = doc.splitTextToSize(safeText(tr), w - 2).slice(0, 2);

    enLines.forEach((txt, i) => {
        doc.text(txt, x + 2.6, y + 2.6 + i * 1.7);
    });

    trLines.forEach((txt, i) => {
        doc.text(txt, x + 2.6, y + 5.0 + i * 1.7);
    });
};

const vText = (doc, text, x, y, fontSize = 4.6) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(fontSize);
    doc.text(safeText(text), x, y, { angle: 90 });
};

const splitAndDraw = (
    doc,
    text,
    x,
    y,
    maxWidth,
    {
        font = "courier",
        style = "normal",
        fontSize = 6.2,
        lineHeight = 3.2,
        maxLines = 20,
        align = "left",
    } = {}
) => {
    doc.setFont(font, style);
    doc.setFontSize(fontSize);

    const lines = doc.splitTextToSize(safeText(text), maxWidth).slice(0, maxLines);
    let currentY = y;

    lines.forEach((lineText) => {
        doc.text(lineText, x, currentY, { align });
        currentY += lineHeight;
    });

    return currentY;
};

const drawLines = (
    doc,
    lines,
    x,
    y,
    maxWidth,
    {
        font = "courier",
        style = "normal",
        fontSize = 6.2,
        lineHeight = 3.2,
        maxLines = 20,
        gap = 0,
    } = {}
) => {
    let currentY = y;
    lines.forEach((item) => {
        currentY = splitAndDraw(doc, item, x, currentY, maxWidth, {
            font,
            style,
            fontSize,
            lineHeight,
            maxLines,
        });
        currentY += gap;
    });
    return currentY;
};

// The CMR is printed in two steps, like the Bill of Lading prints: buildCmrTexts turns a
// shipment into the texts of every box, renderCmrPdf draws those texts on the form. The print
// preview lets the user correct the texts in between; such corrections change only the print.

const MAX_CARGO_ROWS = 4;

const emptyCargoRow = () => ({
    marks: "",
    packages: "",
    packing: "",
    goods: "",
    hsCode: "",
    gross: "",
    volume: "",
});

const joinLines = (lines) => lines.filter(Boolean).join("\n");
const toLines = (text) => String(text || "").split("\n").map((l) => l.trimEnd()).filter((l) => l.trim());

export const buildCmrTexts = (shipmentData = {}) => {
    const cargoDetails = Array.isArray(shipmentData?.cargoDetails) ? shipmentData.cargoDetails : [];
    const sender = safeText(shipmentData?.shipper || shipmentData?.customer);
    const isMultiLtlCmr = shipmentData?.senderSignatureText === "FILENDERS LOGISTICS AND TECHNOLOGY";

    const cargo = (cargoDetails.length ? cargoDetails : [{}]).slice(0, MAX_CARGO_ROWS).map((item) => ({
        ...emptyCargoRow(),
        packages: safeText(item?.unitQuantity || ""),
        packing: safeText(item?.selectedPackingType || ""),
        goods: joinLines([safeText(item?.commodity || ""), safeText(item?.description || "")]),
        hsCode: safeText(item?.hsCode || ""),
        gross: formatNumber(item?.grossWeight, 1),
        volume: formatNumber(item?.cbm, 2),
    }));

    return {
        cmrNumber: safeText(shipmentData?.cmrNumber || shipmentData?.shipmentNo || ""),
        sender,
        consignee: safeText(shipmentData?.consignee),
        placeOfTakingOver: joinLines(addressLines(shipmentData?.pickupLocation)),
        placeOfDelivery: joinLines(addressLines(shipmentData?.deliveryLocation)),
        documentsAttached: "",
        carrier: safeText(shipmentData?.serviceProvider || "FILENDERS LOGISTICS AND TECHNOLOGY"),
        successiveCarrier: "FILENDERS LOGISTICS AND TECHNOLOGY",
        observations: "",
        plate: safeText(shipmentData?.plateContainerNumber || ""),
        cargo,
        senderInstructions: "",
        specialAgreements: "",
        paymentInstructions: "FREIGHT PREPAID",
        establishedPlace: safeText(shipmentData?.establishedPlaceText || "MERSIN, TURKEY"),
        establishedDate: formatDate(shipmentData?.actualDeparture || shipmentData?.establishedDate),
        senderSignature: isMultiLtlCmr ? "FILENDERS LOGISTICS AND TECHNOLOGY" : sender,
    };
};

export const cmrFileName = (texts) => `${safeText(texts?.cmrNumber) || "CMR"}_CMR.pdf`;

export const renderCmrPdf = (texts) => {
    const t = { ...buildCmrTexts({}), ...texts };
    const doc = new jsPDF("p", "mm", "a4");

    doc.setDrawColor(0, 0, 0);
    doc.setTextColor(0, 0, 0);

    const outerX = 12;
    const outerY = 4;
    const outerW = 182;
    const outerH = 292;
    const pageRight = outerX + outerW;

    doc.setLineWidth(0.2);
    box(doc, outerX, outerY, outerW, outerH);
    doc.setLineWidth(0.12);

    vText(doc, "The spaces framed with heavy lines must be filled in by the carrier", 7.2, 70, 4.4);
    vText(doc, "1-15 including", 7.2, 178, 4.8);
    vText(doc, "19-21 + 22", 7.2, 150, 4.8);
    vText(doc, "To be completed on the sender responsibility", 7.2, 228, 4.8);
    vText(
        doc,
        "In case damaged goods are mentioned above in the observations, enter the reservations in box 18.",
        197.2,
        170,
        4.4
    );

    const leftX = 12;
    const leftW = 72;
    const rightX = 84;
    const rightW = 110;

    // 1
    box(doc, leftX, 4, leftW, 32);
    boxTitle(doc, leftX, 4, 1, "Sender (name, address, country)", "Gonderen (isim, adres, memleket)");
    drawLines(doc, toLines(t.sender), leftX + 8, 14.8, leftW - 12, {
        font: "courier",
        fontSize: 6.5,
        lineHeight: 3.6,
        maxLines: 8,
    });

    // Header
    box(doc, rightX, 4, rightW, 22);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.3);
    doc.text("INTERNATIONAL CONSIGNMENT NOTE", rightX + rightW / 2, 9, { align: "center" });
    doc.text("ULUSLARARASI HAMULE SENEDI", rightX + rightW / 2, 14, { align: "center" });

    doc.ellipse(rightX + rightW / 2, 20.5, 14, 5);
    doc.setFontSize(12);
    doc.text("CMR", rightX + rightW / 2, 21.7, { align: "center" });

    doc.setFont("courier", "normal");
    doc.setFontSize(6.3);
    doc.text(safeText(t.cmrNumber), rightX + 10, 30);

    // 2
    box(doc, leftX, 36, leftW, 30);
    boxTitle(doc, leftX, 36, 2, "Consignee (name, address, country)", "Alacak Olan (isim, adres, memleket)");
    drawLines(doc, toLines(t.consignee), leftX + 8, 46.5, leftW - 12, {
        font: "courier",
        fontSize: 6.5,
        lineHeight: 3.6,
        maxLines: 8,
    });

    // 3
    box(doc, leftX, 66, leftW, 22);
    boxTitle(
        doc,
        leftX,
        66,
        3,
        "Place of taking over the goods (place, country, date)",
        "Mallarin teslim yeri (mahal, memleket, tarih)"
    );
    drawLines(doc, toLines(t.placeOfTakingOver), leftX + 8, 76.2, leftW - 12, {
        font: "courier",
        fontSize: 6.3,
        lineHeight: 3.4,
        maxLines: 5,
    });

    // 4
    box(doc, leftX, 88, leftW, 22);
    boxTitle(
        doc,
        leftX,
        88,
        4,
        "Place and date of delivery of the goods",
        "Mallarin teslim alindigi yer (mahal, memleket, tarih)"
    );
    drawLines(doc, toLines(t.placeOfDelivery), leftX + 8, 98.2, leftW - 12, {
        font: "courier",
        fontSize: 6.3,
        lineHeight: 3.4,
        maxLines: 5,
    });

    // 5
    box(doc, leftX, 110, leftW, 15);
    boxTitle(doc, leftX, 110, 5, "Documents attached", "Ek belgeler");
    drawLines(doc, toLines(t.documentsAttached), leftX + 8, 118.5, leftW - 12, {
        font: "courier",
        fontSize: 6,
        lineHeight: 3.1,
        maxLines: 2,
    });

    // 16 + 17
    box(doc, rightX, 36, rightW, 57);
    boxTitle(doc, rightX, 36, 16, "Carrier (name, address, country)", "Tasiyici Firma (isim, adres, memleket)");
    drawLines(doc, toLines(t.carrier), rightX + 8, 46.3, rightW - 14, {
        font: "courier",
        fontSize: 6,
        lineHeight: 3.1,
        maxLines: 6,
    });

    line(doc, rightX, 67.5, rightX + rightW, 67.5);

    boxTitle(
        doc,
        rightX,
        67.5,
        17,
        "Successive carriers (name, address, country)",
        "Muteakip / ikinci tasiyici firma (isim, adres, memleket)"
    );

    drawLines(doc, toLines(t.successiveCarrier), rightX + 8, 78, rightW - 14, {
        font: "courier",
        fontSize: 6,
        lineHeight: 3.1,
        maxLines: 6,
    });
    doc.addImage(logoImg, "PNG", rightX + rightW - 40, 74, 38, 14);

    // 18
    box(doc, rightX, 93, rightW, 32);
    boxTitle(
        doc,
        rightX,
        93,
        18,
        "Carrier reservations and observations",
        "Tasiyicinin mulahazat ve musahedeleri"
    );
    drawLines(doc, toLines(t.observations), rightX + 8, 102.5, rightW - 14, {
        font: "courier",
        fontSize: 6,
        lineHeight: 3.1,
        maxLines: 3,
    });

    doc.setFont("courier", "normal");
    doc.setFontSize(6.2);
    doc.text("Plate Number:", rightX + 8, 117.5);
    doc.text(safeText(t.plate), rightX + 38, 117.5);

    // ===================== 6-12 CARGO TABLE =====================
    const cargoY = 125;
    const cargoH = 63;
    const totalCargoW = pageRight - 12;
    const unitW = totalCargoW / 7;

    const x678 = 12;
    const w678 = unitW * 3;

    const x9 = x678 + w678;
    const w9 = unitW;

    const x10 = x9 + w9;
    const w10 = unitW;

    const x11 = x10 + w10;
    const w11 = unitW;

    const x12 = x11 + w11;
    const w12 = pageRight - x12;

    box(doc, x678, cargoY, w678, cargoH);
    box(doc, x9, cargoY, w9, cargoH);
    box(doc, x10, cargoY, w10, cargoH);
    box(doc, x11, cargoY, w11, cargoH);
    box(doc, x12, cargoY, w12, cargoH);

    const cargoHeaderH = 12;
    const cargoHeaderY = cargoY + cargoHeaderH;
    line(doc, x678, cargoHeaderY, pageRight, cargoHeaderY);

    drawMergedHeaderSegment(doc, x678 + unitW * 0, cargoY, unitW, 6, "Marks and No's", "Marka ve No.");
    drawMergedHeaderSegment(doc, x678 + unitW * 1, cargoY, unitW, 7, "Number of packages", "Koli / paket adedi");
    drawMergedHeaderSegment(doc, x678 + unitW * 2, cargoY, unitW, 8, "Method of packing", "Ambalaj sekli / metodu");

    drawTinyHeader(doc, x9, cargoY, w9, 9, "Nature of goods", "Malin mahiyeti / cinsi");
    drawTinyHeader(doc, x10, cargoY, w10, 10, "Statistical number", "Istatistik numarasi");
    drawTinyHeader(doc, x11, cargoY, w11, 11, "Gross weight in kg.", "Kg. olarak brut agirlik");
    drawTinyHeader(doc, x12, cargoY, w12, 12, "Volume, in m3", "M3 olarak hacim");

    const adrY = cargoY + cargoH - 8;
    line(doc, x678, adrY, x9, adrY);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    doc.text("Class", x678 + 1.2, adrY + 3);
    doc.text("Number", x678 + unitW + 1.2, adrY + 3);
    doc.text("Latter", x678 + unitW * 2 + 1.2, adrY + 3);
    doc.text("(ADR)", x678 + unitW * 2 + 10.2, adrY + 3);

    let rowY = cargoY + 20;

    (t.cargo || []).slice(0, MAX_CARGO_ROWS).forEach((row) => {
        const item = { ...emptyCargoRow(), ...row };
        const small = { font: "courier", fontSize: 6, lineHeight: 3.1, maxLines: 2 };

        doc.setFont("courier", "normal");
        doc.setFontSize(6);

        if (item.marks) splitAndDraw(doc, item.marks, x678 + 1.5, rowY, unitW - 3, small);
        if (item.packages) splitAndDraw(doc, item.packages, x678 + unitW + 1.5, rowY, unitW - 3, small);
        if (item.packing) splitAndDraw(doc, item.packing, x678 + unitW * 2 + 1.5, rowY, unitW - 3, small);

        if (item.goods) {
            splitAndDraw(doc, item.goods, x9 + 1.2, rowY, w9 - 2.4, {
                font: "courier",
                fontSize: 5.8,
                lineHeight: 3.0,
                maxLines: 4,
            });
        }

        if (item.hsCode) {
            splitAndDraw(doc, item.hsCode, x10 + 1.2, rowY, w10 - 2.4, {
                font: "courier",
                fontSize: 5.8,
                lineHeight: 3.0,
                maxLines: 2,
            });
        }

        doc.setFont("courier", "normal");
        doc.setFontSize(6);
        if (item.gross) doc.text(safeText(item.gross), x11 + w11 - 2, rowY, { align: "right" });
        if (item.volume) doc.text(safeText(item.volume), x12 + w12 - 2, rowY, { align: "right" });

        rowY += 12;
    });

    // ===================== ALT BLOKLAR =====================

    // 13
    box(doc, 12, 188, 70, 40);
    boxTitle(doc, 12, 188, 13, "Sender instructions", "Gonderenin talimati");
    drawLines(doc, toLines(t.senderInstructions), 20, 198, 58, {
        font: "courier",
        fontSize: 6,
        lineHeight: 3.1,
        maxLines: 9,
    });

    const rightBottomX = 82;
    const rightBottomW = pageRight - rightBottomX;

    // 19
    box(doc, rightBottomX, 188, rightBottomW, 18);
    drawMediumHeader(doc, rightBottomX, 188, rightBottomW, 19, "Special agreements", "Ozel sartlar / anlasma");
    drawLines(doc, toLines(t.specialAgreements), rightBottomX + 8, 197, rightBottomW - 12, {
        font: "courier",
        fontSize: 6,
        lineHeight: 3.1,
        maxLines: 3,
    });

    // 20 - expanded
    const payX = rightBottomX;
    const payY = 206;
    const payW = rightBottomW;
    const payH = 31;

    box(doc, payX, payY, payW, payH);
    drawMediumHeader(doc, payX, payY, payW, 20, "To be paid by :", "Odeenecek");

    const payContentTop = payY + 11;
    const payBottom = payY + payH;

    const descW = 38;
    const senderW = 18;
    const currencyW = 20;
    const consigneeW = payW - descW - senderW - currencyW;

    const colX1 = payX + descW;
    const colX2 = colX1 + senderW;
    const colX3 = colX2 + currencyW;

    line(doc, payX, payContentTop, payX + payW, payContentTop);

    line(doc, colX1, payY, colX1, payBottom);
    line(doc, colX2, payY, colX2, payBottom);
    line(doc, colX3, payY, colX3, payBottom);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(3.9);
    doc.text("Sender", colX1 + senderW / 2, payContentTop - 1.8, { align: "center" });
    doc.text("Currency", colX2 + currencyW / 2, payContentTop - 1.8, { align: "center" });
    doc.text("Consignee", colX3 + consigneeW / 2, payContentTop - 1.8, { align: "center" });

    const payGroups = [
        { lines: ["Carrige Charges", "Navlun Tutari"] },
        { lines: ["Balance", "Bakiye"] },
        { lines: ["Supplem. Charges", "Muntazam Masraflar"] },
        { lines: ["TOTAL", "Toplam"] },
    ];

    const groupH = (payBottom - payContentTop) / payGroups.length;

    for (let i = 1; i < payGroups.length; i += 1) {
        const yy = payContentTop + i * groupH;
        line(doc, payX, yy, payX + payW, yy);
    }

    payGroups.forEach((group, groupIndex) => {
        const top = payContentTop + groupIndex * groupH;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(3.7);

        const innerTopPad = 1.2;
        const innerBottomPad = 1.0;
        const usableH = groupH - innerTopPad - innerBottomPad;
        const lineGap = usableH / group.lines.length;

        group.lines.forEach((txt, i) => {
            const textY = top + innerTopPad + (i + 0.72) * lineGap;
            doc.text(txt, payX + 1, textY);
        });
    });

    // 14
    const box14X = 12;
    const box14Y = 228;
    const box14W = 70;
    const box14H = 18;

    box(doc, box14X, box14Y, box14W, box14H);
    boxTitle(doc, box14X, box14Y, 14, "Instructions as to payment for carriage", "Navlunun odeme sekli");

    doc.setFont("courier", "normal");
    doc.setFontSize(5.6);
    if (t.paymentInstructions) {
        doc.text(safeText(t.paymentInstructions), box14X + box14W - 4, box14Y + 10.2, { align: "right" });
    }

    // 15 - moved down
    const reimbY = payY + payH;
    const reimbH = 246 - reimbY;

    box(doc, rightBottomX, reimbY, rightBottomW, reimbH);
    drawMediumHeader(
        doc,
        rightBottomX,
        reimbY,
        rightBottomW,
        15,
        "Reimbursement / cash on delivery",
        "Teslimatta odenecek meblag"
    );

    // 21
    box(doc, 12, 246, 70, 12);
    boxTitle(doc, 12, 246, 21, "Established in", "Tanzim Yeri");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(5.2);
    doc.text("on", 55, 250.2);

    doc.setFont("courier", "normal");
    doc.setFontSize(6);
    doc.text(safeText(t.establishedPlace), 20, 255);
    doc.text(safeText(t.establishedDate), 60, 255);

    // 22 23 24
    box(doc, 12, 258, 50, 38);
    box(doc, 62, 258, 50, 38);
    box(doc, 112, 258, 82, 38);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    doc.text("22", 14, 262);
    doc.text("23", 64, 262);
    doc.text("24", 114, 262);

    drawLines(doc, toLines(t.senderSignature).slice(0, 4), 20, 270, 38, {
        font: "courier",
        fontSize: 5.8,
        lineHeight: 3.2,
        maxLines: 5,
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(5);
    doc.text("Signature and stamp of the sender", 14, 290);
    doc.text("Gonderenin imza ve kasesi", 14, 293.2);

    doc.text("Signature and stamp of the carrier", 64, 290);
    doc.text("Tasiyicinin imza ve kasesi", 64, 293.2);

    doc.text("Goods received / Alinan yuk", 124, 262);
    doc.text("Place", 116, 276);
    doc.text("on", 150, 276);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(5);
    doc.text("Signature and stamp of the consignee", 114, 290);
    doc.text("Tasiyicinin imza ve kasesi", 114, 293.2);

    return doc;
};

// Object URL of the drawn CMR, for the print preview; the caller revokes it.
export const cmrPdfUrl = (texts) => URL.createObjectURL(renderCmrPdf(texts).output("blob"));

export const downloadCmrPdf = (texts) => renderCmrPdf(texts).save(cmrFileName(texts));

// Prints straight away, without the preview.
export const generateCmrPdf = async (shipmentData) => downloadCmrPdf(buildCmrTexts(shipmentData));
